import { Button } from "@myloc/myloc-gui";
import { ReactNode } from "react";
import { useTranslate } from "../../../language/i18n";
import AccordionItem from "../AccordionItem/AccordionItem";
import CommentList, { Comment } from "./CommentList/CommentList";
import styles from "./Comments.module.scss";
import useCommentActions from "./useCommentActions";

export interface CreateModalProps {
  visible: boolean;
  title: string;
  onSubmit: (comment: string) => void;
  onCancel: () => void;
}

export interface DeleteModalProps {
  visible: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}

interface CommentsProps {
  title: string;
  createModalTitle?: string;
  comments?: Comment[];
  accordion?: boolean;
  onAdd?: (comment: string) => Promise<boolean>;
  onDelete?: (comment: Comment) => Promise<boolean>;
  createModal?: (props: CreateModalProps) => ReactNode;
  deleteModal?: (props: DeleteModalProps) => ReactNode;
}

const Comments = ({
  title,
  createModalTitle,
  comments,
  accordion = true,
  onAdd,
  onDelete,
  createModal,
  deleteModal,
}: CommentsProps) => {
  const translate = useTranslate();
  const {
    createVisible,
    setCreateVisible,
    deleteVisible,
    setDeleteVisible,
    handleAdd,
    handleDeleteClicked,
    handleDelete,
  } = useCommentActions(onAdd, onDelete);

  const canCreate = !!(onAdd && createModal);

  if (!comments?.length && !canCreate) return null;

  const content = (
    <>
      <CommentList comments={comments} onDelete={onDelete ? handleDeleteClicked : undefined} />
      {canCreate && (
        <>
          <div className={styles.button}>
            <Button onClick={() => setCreateVisible(true)} customCssClass="" isLoading={false}>
              {translate("CREATE")}
            </Button>
          </div>
          {createModal({
            visible: createVisible,
            title: createModalTitle ?? title,
            onSubmit: handleAdd,
            onCancel: () => setCreateVisible(false),
          })}
        </>
      )}
      {onDelete &&
        deleteModal?.({ visible: deleteVisible, onSubmit: handleDelete, onCancel: () => setDeleteVisible(false) })}
    </>
  );

  if (accordion) {
    return (
      <AccordionItem title={title} noPadding>
        {content}
      </AccordionItem>
    );
  }

  return (
    <section>
      <h3 className={styles.title}>{title}</h3>
      {content}
    </section>
  );
};

export default Comments;
